import * as turf from '@turf/turf';

export class MISpatialAnalysis {
  static buffer(geojson: GeoJSON.Feature | GeoJSON.Geometry, distance: number, options?: { units?: string; steps?: number }): GeoJSON.Feature {
    const feature = geojson.type === 'Feature' ? geojson : { type: 'Feature' as const, properties: {}, geometry: geojson };
    return turf.buffer(feature as any, distance, {
      units: (options?.units as any) || 'kilometers',
      steps: options?.steps || 64,
    }) as any;
  }

  static union(geojson1: GeoJSON.Feature, geojson2: GeoJSON.Feature): GeoJSON.Feature | null {
    try {
      return turf.union(turf.featureCollection([geojson1 as any, geojson2 as any])) as any;
    } catch {
      return null;
    }
  }

  static intersection(geojson1: GeoJSON.Feature, geojson2: GeoJSON.Feature): GeoJSON.Feature | null {
    try {
      return turf.intersect(turf.featureCollection([geojson1 as any, geojson2 as any])) as any;
    } catch {
      return null;
    }
  }

  static difference(geojson1: GeoJSON.Feature, geojson2: GeoJSON.Feature): GeoJSON.Feature | null {
    try {
      return turf.difference(turf.featureCollection([geojson1 as any, geojson2 as any])) as any;
    } catch {
      return null;
    }
  }

  static convexHull(geojson: GeoJSON.FeatureCollection): GeoJSON.Feature | null {
    try {
      return turf.convex(geojson as any) as any;
    } catch {
      return null;
    }
  }

  static centroid(geojson: GeoJSON.Feature | GeoJSON.Geometry): GeoJSON.Feature {
    const feature = geojson.type === 'Feature' ? geojson : { type: 'Feature' as const, properties: {}, geometry: geojson };
    return turf.centroid(feature as any) as any;
  }

  static area(geojson: GeoJSON.Feature | GeoJSON.Geometry): number {
    const feature = geojson.type === 'Feature' ? geojson : { type: 'Feature' as const, properties: {}, geometry: geojson };
    return turf.area(feature as any);
  }

  static getLength(geojson: GeoJSON.Feature | GeoJSON.Geometry, units: string = 'kilometers'): number {
    const feature = geojson.type === 'Feature' ? geojson : { type: 'Feature' as const, properties: {}, geometry: geojson };
    return turf.length(feature as any, { units: units as any });
  }

  static distance(from: [number, number], to: [number, number], units: string = 'kilometers'): number {
    return turf.distance(turf.point(from), turf.point(to), { units: units as any });
  }

  static voronoi(points: GeoJSON.FeatureCollection, bbox?: [number, number, number, number]): GeoJSON.FeatureCollection {
    const options: any = {};
    if (bbox) {
      options.bbox = bbox;
    }
    return turf.voronoi(points as any, options) as any;
  }

  static nearestPoint(targetPoint: [number, number], points: GeoJSON.FeatureCollection): GeoJSON.Feature {
    return turf.nearestPoint(turf.point(targetPoint), points as any) as any;
  }

  static pointsWithinPolygon(points: GeoJSON.FeatureCollection, polygon: GeoJSON.Feature): GeoJSON.FeatureCollection {
    return turf.pointsWithinPolygon(points as any, polygon as any) as any;
  }

  static booleanContains(feature1: GeoJSON.Feature, feature2: GeoJSON.Feature): boolean {
    return turf.booleanContains(feature1 as any, feature2 as any);
  }

  static booleanIntersects(feature1: GeoJSON.Feature, feature2: GeoJSON.Feature): boolean {
    return turf.booleanIntersects(feature1 as any, feature2 as any);
  }

  static booleanWithin(feature1: GeoJSON.Feature, feature2: GeoJSON.Feature): boolean {
    return turf.booleanWithin(feature1 as any, feature2 as any);
  }

  static simplify(geojson: GeoJSON.Feature, tolerance: number = 0.01): GeoJSON.Feature {
    return turf.simplify(geojson as any, { tolerance, highQuality: true }) as any;
  }

  static along(line: GeoJSON.Feature, distance: number, units: string = 'kilometers'): GeoJSON.Feature {
    return turf.along(line as any, distance, { units: units as any }) as any;
  }

  static bbox(geojson: GeoJSON.Feature | GeoJSON.FeatureCollection): [number, number, number, number] {
    return turf.bbox(geojson as any) as [number, number, number, number];
  }

  static bboxPolygon(bbox: [number, number, number, number]): GeoJSON.Feature {
    return turf.bboxPolygon(bbox) as any;
  }

  static midpoint(point1: [number, number], point2: [number, number]): GeoJSON.Feature {
    return turf.midpoint(turf.point(point1), turf.point(point2)) as any;
  }

  static bearing(start: [number, number], end: [number, number]): number {
    return turf.bearing(turf.point(start), turf.point(end));
  }

  static destination(origin: [number, number], distance: number, bearing: number, units: string = 'kilometers'): GeoJSON.Feature {
    return turf.destination(turf.point(origin), distance, bearing, { units: units as any }) as any;
  }

  static dissolve(fc: GeoJSON.FeatureCollection): GeoJSON.FeatureCollection {
    return turf.dissolve(fc as any) as any;
  }

  static transformRotate(geojson: GeoJSON.Feature, angle: number, options?: { pivot?: [number, number] }): GeoJSON.Feature {
    return turf.transformRotate(geojson as any, angle, options as any) as any;
  }

  static transformScale(geojson: GeoJSON.Feature, factor: number): GeoJSON.Feature {
    return turf.transformScale(geojson as any, factor) as any;
  }
}
